import classNames from 'classnames'
import { motion } from 'framer-motion'
import { PropsWithChildren, useEffect, useRef } from 'react'

import { SafeAreaTopSpacer } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { useWindowDimensions } from '@apps-common/hooks/useWindowDimensions'
import { useMenuStore } from '@apps-common/stores/menuStore'
import { ROUTE_DASHBOARD } from '@apps-radionet-portal/config/private-routes'

interface ItemProps {
  showBack?: boolean
  transparent?: boolean
  icons?: any
  startComponents?: any
  children?: any
  onHeightChange?: (height?: number) => void
}

export const NavbarMyMonitoring = ({
  showBack,
  transparent,
  icons,
  startComponents,
  children,
  onHeightChange
}: PropsWithChildren<ItemProps>) => {
  const { width, isMobile } = useWindowDimensions()
  const { navigateBack } = useNavigation()
  const { open, setOpen } = useMenuStore()
  const containerRef = useRef<HTMLDivElement>(null)
  const BackIcon = commonIcons.back

  useEffect(() => {
    if (!onHeightChange) return

    const element = containerRef.current
    if (!element) return

    const updateHeight = () => {
      onHeightChange(element.getBoundingClientRect().height)
    }

    updateHeight()

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeight) : null
    resizeObserver?.observe(element)
    window.addEventListener('resize', updateHeight)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateHeight)
    }
  }, [children, icons, onHeightChange, showBack, startComponents])

  return (
    <div
      ref={containerRef}
      className='absolute top-0 block z-[20] w-full pb-[25px] bg-gradient-to-b from-bg sm:from-transparent from-15% to-bg/20 sm:to-transparent to-100% backdrop-blur-lg sm:backdrop-blur-none rounded-b-[50px] sm:rounded-none shadow-lg sm:shadow-none shadow-text/[0.025]'
    >
      <motion.div
        animate={{
          opacity: open ? 0.9 : 1,
          transition: { ease: 'easeInOut', duration: 0.3 }
        }}
        className={classNames('z-[1] text-text w-full px-[1rem] sm:px-[20px]')}
        onClick={() => {
          if (open) {
            setOpen(false)
          }
        }}
      >
        <SafeAreaTopSpacer />
        <div className='flex h-[40px] md:h-[55px] items-start justify-center transition-all duration-100 mt-[5px] sm:mt-[20px]'>
          <div className='w-full flex items-start justify-between'>
            <div className='flex-none'>
              <label htmlFor='my-drawer' className='drawer-button flex items-center justify-start gap-[5px]'>
                {showBack && (
                  <button
                    type='button'
                    aria-label='Back'
                    className='w-[40px] h-[40px] rounded-[25px] bg- backdrop-blur-md flex items-center justify-center hover:opacity-70'
                    onClick={() => navigateBack({ routeUrl: ROUTE_DASHBOARD.buildPath(), route: ROUTE_DASHBOARD })}
                  >
                    <BackIcon className='text-[1.3rem]' />
                  </button>
                )}
              </label>
              {/* <FloatingMenu /> */}
              <div>{startComponents}</div>
            </div>
            <div className='flex items-center justify-end'>{icons}</div>
          </div>
        </div>
        {children}
      </motion.div>
    </div>
  )
}
